import React, { useEffect, useState } from 'react';
import { Main } from '../../styled';
import { Row, Col, Form, Input, Radio, Select, Modal, Table, Switch } from 'antd';
import { useDispatch } from 'react-redux';
import Spinner from '../../common/Spinner';
import Notification from '../../../utility/notification';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { Button } from '../../../components/buttons/buttons';
import HttpStatus from '../../../utility/status.enum';
import { updateExportSettings, getExportSettings, getExportLogs } from '../../../redux/manageExport/actionCreator';
import { InfoCircleOutlined } from '@ant-design/icons';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { fetchCmsContent } from '../../../redux/manageCms/actionCreator';
import { useParams } from 'react-router-dom';
import Functions from '../../../utility/functions';

const ExportSettings = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const hostSlug = useParams()?.hostSlug ?? "";
  const [spinnerLoader, setSpinnerLoader] = useState(false); 
  const [loginUser, setLoginUser] = useState({}); // Logged in user data
  const [exportSettingData, setExportSettingData] = useState({}); // Export settings data
  const [isUserExportModalVisible, setUserExportModalVisible] = useState(false);  // Modal for export logs
  const [exportActivities, setExportActivities] = useState([]);  // Export activities data
  const [statusId, setStatusId] = useState(2);  // Status ID for export settings
  const [exportAboutPopup, setExportAboutPopup] = useState(false); // Popup for export information
  const [dataExportText, setDataExportText] = useState(''); // Text for export information popup
  const [exportEnable, setExportEnable] = useState(false); // State to handle no export option

  // Time dropdown values
  const hoursArray = Array.from({ length: 24 }, (_, index) => {   // Generate an array of hours in 12-hour format
    let hour = index % 12;
    if (hour === 0) hour = 12;
    const period = index < 12 ? 'AM' : 'PM';
    return `${hour.toString().padStart(2, '0')}:00 ${period}`;
  });

  useEffect(() => {
    const getUser = JSON.parse(localStorage.getItem('user'));   // Get logged-in user data from local storage
    if (getUser) {
      setLoginUser(getUser);
      fetchExportSettings();
    }
  }, [hostSlug]);

  const fetchExportSettings = async () => { // Fetch export settings for the logged-in user
    setSpinnerLoader(true);
    try {
      const parms = hostSlug ? '?host_slug=' + hostSlug : '';
      const response = await dispatch(getExportSettings(parms)); // Get export settings 
      if (response?.status === HttpStatus.HTTP_OK) {
        setExportSettingData(response.result);
        setStatusId(response.result.status_id);   // Set initial status ID
        form.setFieldsValue({
          type: response.result.type,
          email: response.result.email,
          time: response.result.time,
        });
        if(response?.result?.type === 1) {
          setExportEnable(false); // Disable export settings if type is "No Export"
        } else {
          setExportEnable(true); // Enable export settings if type is not "No Export"
        }
      }
    } catch (error) {
      Notification.addNotificationError('Failed to fetch export settings');
    } finally {
      setSpinnerLoader(false);
    }
  };

  const onFinish = async (values) => {   // Handle form submission
    setSpinnerLoader(true);
    try {
      const payload = {
        type: values.type, // 1 - No Export, 2 - Full Export, 3 - Incremental Export
        email: values.email,
        time: values.time,
        host_slug: hostSlug,
      };
      const response = await dispatch(updateExportSettings(payload)); // Update export settings
      if (response?.status === HttpStatus.HTTP_OK) {
        Notification.addNotificationSuccess('Settings updated successfully');
        fetchExportSettings(); // Retrieve updated export settings
      } else {
        Notification.addNotificationError(response?.message || 'Failed to update export settings');
      }
    } catch (error) {
      Notification.addNotificationError('Failed to update export settings');
    } finally {
      setSpinnerLoader(false);
    }
  };

  const exportColumns = [
    {
      title: 'Export Type',
      dataIndex: 'report_type',
      key: 'report_type',
    },
    {
      title: 'Exported at (ET)',
      dataIndex: 'export_date_time',
      key: 'export_date_time',
    },
    {
      title: 'Total Reservations',
      dataIndex: 'reservation_count',
      key: 'reservation_count',
    },
  ];

  const openUserExportModal = async () => {  // Open the export logs modal
    setUserExportModalVisible(true);
    if (loginUser?.slug) {
      setSpinnerLoader(true);
      try {
        const parms = hostSlug ? '?host_slug=' + hostSlug : '';
        const response = await dispatch(getExportLogs(parms)); // Fetch export logs 
        if (response?.status === HttpStatus.HTTP_OK) {
          setExportActivities(response?.result || []);
        } else {
          Notification.addNotificationError(response?.message || 'Failed to fetch export logs');
        }
      } catch (error) {
        Notification.addNotificationError('Failed to fetch export logs');
      } finally {
        setSpinnerLoader(false);
      }
    } 
  };

  const closeUserExportModal = () => {  // Close the export logs modal
    setUserExportModalVisible(false);
  };

  const showEarlyCheckingInfo = (actionType) => {
      setSpinnerLoader(true);
      const fetchCmsContentData = async () => {
        try {
          const fetchCmsContentResponse = await dispatch(
            fetchCmsContent({
              action_type: actionType,
            }),
          );
          if (fetchCmsContentResponse.status === HttpStatus.HTTP_OK) {
            const resultData = fetchCmsContentResponse?.result?.description;
            const hasIntercomArticleId = Functions.findIntercomArticleId(resultData);   
            if(!hasIntercomArticleId){ // Check if CMS content has an Intercom ID
              setDataExportText(resultData);
              setExportAboutPopup(true);
            }       
            setSpinnerLoader(false);
          } 
        } catch (error) { }
      };
      fetchCmsContentData();
    };

  const handleLinkClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const closeEarlyCheckInPopupInfo = () => {
    setExportAboutPopup(false);
  };

  const handleChangeExportType = (e) => {
    const {value} = e.target;
    console.log('value', value);
    if(!value) return ;
    if(value === 1) {
      setExportEnable(false); // Disable export settings if type is "No Export"
    } else {
      setExportEnable(true); // Enable export settings if type is not "No Export"
    }
  }

  return (
    <>
      <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            type: 1,
            email: '',
            time: '',
          }}
        >
        <Cards xs={24} headless className='borderRadius common_fiter_table'>
        <Main className='crm-export-section'>
          {/* <div className="d-flex pageheader justify-content-end">
            <PageHeader className="pageheaderButton" />
          </div> */}
          {/* <Cards headless className='borderRadius common_fiter_table'> */}
                <Spinner loader={spinnerLoader} />
                <h4 className="mb-20">CRM Exports Settings</h4>
                {/* <hr /> */}
              <Row gutter={15}>
                <Col xs={24}>
                  <Form.Item
                    name="type"
                    rules={[{ required: true, message: 'Please select export type' }]}
                  >
                    <Radio.Group className='radio-data-export' onChange={(e) => handleChangeExportType(e)}>
                      <Radio value={1}>No Export</Radio>
                      <Radio value={2}>Export ALL guest contact info from completed check-in forms every day</Radio>
                      <Radio value={3}>Export ONLY NEWLY completed guest contact info every day (the first export will include all form completions)</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              {
                exportEnable && (
                  <>
                    <Row gutter={15}>
                      <Col xs={24} md={12}>
                        <div className="project-modal">
                          <Form.Item
                            label="Send CRM Export to email"
                            name="email"
                            rules={[
                              {
                                required: true,
                                message: 'Please enter email',
                                type: 'email',
                              },
                            ]}
                          >
                            <Input placeholder="Enter email" />
                          </Form.Item>
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={15}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Exported email will send every day at this time (ET)" name="time" rules={[{ required: true, message: 'Please select time' }]}>
                          <Select size="large" className="ninjadash-fullwidth-select" placeholder="Select time" allowClear>
                            {hoursArray.map((value) => (
                              <Select.Option key={value} value={value}>
                                {value}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              }
              <Row gutter={15}>
                <Col xs={24} md={12}>
                  <a
                    className="common_urlLink"
                    onClick={(event) => {
                      showEarlyCheckingInfo(11);
                      handleLinkClick(event);
                    }}
                  >
                    (Learn: How it works)
                  </a>
                  <br />
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={12}>
                  <div className="reject-reason-info">
                    <span>CRM Export History</span>
                    <InfoCircleOutlined
                      style={{ cursor: 'pointer' }}
                      onClick={() => openUserExportModal()}
                      className="info-logo"
                    />
                  </div>
                </Col>
              </Row>
          {/* </Cards> */}
        </Main>
        </Cards>
        <Button htmlType="submit" type="primary" size="default" shape="circle" className="export-save-button w-20">
          Save
        </Button>
      </Form>
      <Modal
        type="primary"
        open={isUserExportModalVisible}
        footer={null}
        onCancel={closeUserExportModal}
        width={'60%'}
        maskClosable={false}
        key={2}
        aria-hidden="false"
        centered
      >
        <div className="Idmodalbox earlyCheckinPopup">
          <div className="hostPayout">
            <h3>CRM Export History</h3>
            <Table
              columns={exportColumns}
              dataSource={exportActivities}
              rowKey={(record, idx) => idx}
              pagination={false}
              size="small"
              className='ant-table-wrapper table-responsive coverage-table'
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={exportAboutPopup}
        footer={null}
        onCancel={closeEarlyCheckInPopupInfo}
        className="modal-md"
        key={2}
        maskClosable={false}
        aria-hidden="false"
      >
        <div className="Idmodalbox earlyCheckinPopup">
          <div className="hostPayout">
            <div className="modal-content" dangerouslySetInnerHTML={{ __html: dataExportText }} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExportSettings;
